import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { type ReactNode, useEffect, useRef } from 'react';

import { useLazyProfileQuery } from './authApi';
import { authBootStart, authFailed, authSucceeded } from './authSlice';
import { tokenStorage } from './utils/tokenStorage';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

export function AuthBootstrap({ children, fallback = null }: Props) {
  const dispatch = useAppDispatch();
  const status = useAppSelector((s) => s.auth.status);
  const [fetchProfile] = useLazyProfileQuery();
  // A ref (rather than a cancellation flag) is used on purpose: StrictMode
  // re-invokes effects, and tying the cancel token to the closure caused the
  // async `fetchProfile()` result to be discarded, leaving status stuck on
  // `'booting'`. The bootstrap is intentionally a one-shot — subsequent
  // auth changes go through login/logout flows, never back to 'idle'.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    if (status !== 'idle') return;
    started.current = true;

    const run = async () => {
      if (!tokenStorage.getAccessToken() && !tokenStorage.getRefreshToken()) {
        dispatch(authFailed(null));
        return;
      }

      dispatch(authBootStart());

      try {
        const result = await fetchProfile().unwrap();
        dispatch(authSucceeded(result));
      } catch {
        tokenStorage.clear();
        dispatch(authFailed(null));
      }
    };

    void run();
  }, [dispatch, fetchProfile, status]);

  if (status === 'idle' || status === 'booting') {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
